import type { TranslationKey } from '@/services/types'
import type { Dict } from '@/types'
import type { ValidationInitiator } from '@/services/forms/types'

export interface FieldValidationResult {
  valid: boolean
  errors: ValidationRuleErrorWithRuleName[]
  rulesResults: Dict<boolean> // { [ruleName: string]: boolean }
}

export interface RuleConfig {
  messagePath?: TranslationKey
  messageParams?: Record<string, unknown>
  showFirstMessageFromNested?: boolean
  ignoreOnSubmit?: boolean // Errors are shown, but ignored on submit
  data?: Dict
}

export type ValidationRule = {
  name: string
  customCallBack?: ValidationRuleFunctionCustom<any, boolean>
} & RuleConfig

export type IgnoreOnSubmitValidationPayload = {
  ignoreOnSubmit: true
}

export type ValidationRuleError<P = unknown> = {
  message: {
    path: string // @todo use TranslationKey in separate task
    params?: Record<string, unknown>
    tc?: number
  }
  payload?: P
  ruleName?: string
}

export const hasIgnoreOnSubmitPayload = (error: ValidationRuleError): boolean => {
  const payload = error.payload
  if (!payload || typeof payload !== 'object') {
    return false
  }
  return (payload as IgnoreOnSubmitValidationPayload).ignoreOnSubmit === true
}

export type ValidationRuleFunctionResult<P = unknown> =
  | boolean
  | ValidationRuleError<P>
  | Promise<boolean | ValidationRuleError<P> | ValidationRuleError<P>[]>
export type ValidationRuleFunction<P = unknown> = (
  value: any,
  params: any,
  formValues: Record<string, any>,
  config?: RuleConfig,
  validationInitiator?: ValidationInitiator,
) => ValidationRuleFunctionResult<P>
export type ValidationRuleFunctionCustom<TValue = unknown, P = unknown> = (
  value: TValue,
  formValues: Record<string, any>,
  config?: RuleConfig,
  validationInitiator?: ValidationInitiator,
) => ValidationRuleFunctionResult<P>
export type ValidationRulesMap = Map<string, ValidationRuleFunction>

export interface ValidationOptions {
  crossFieldValues?: Record<string, string>
  values: Record<string, any>
}

export interface ValidationParams<T = any> {
  value: T
  validators: ValidationRule[]
  formValues: Record<string, string>
  crossFieldValues?: Record<string, string>
  localValidators?: ValidationRulesMap
  validationInitiator?: ValidationInitiator
}

export type ValidationRuleErrorWithRuleName = {
  ruleName: string
} & ValidationRuleError

export interface RawFieldValidationResult {
  valid: boolean
  errors: ValidationRuleErrorWithRuleName[]
}

export type ValidationServerError<P = unknown> = string | ValidationRuleError<P>
